import {
  BubbleMenu,
  EditorContent,
  posToDOMRect,
  Editor,
  FloatingMenu,
} from '@tiptap/react';
import { FormEvent, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ProgressCircle } from './ProgressCircle';
import { AddLink, AddLinkDisabled } from 'assets/images';
import { UnstyledButton } from '../UnstyledButton';
import { AddLinkPopup } from './AddLinkPopup';
import { useTipTap } from './useTipTap';
// Should move this to some constants file
// Not sure where though
const CHARACTER_LIMIT = 200;

export const TextEditor = ({
  changeHandler,
  defaultValue,
  isInvalid,
}: {
  changeHandler: (value: string | FormEvent<HTMLInputElement>) => void;
  defaultValue: string;
  isInvalid?: boolean;
}) => {
  const editor = useTipTap(changeHandler, defaultValue);

  const [isAddingLink, setIsAddingLink] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>();

  /**
   * @param editor Editor
   * @param {string} link URL to be added to the text
   * @param {string} displayText Text to be displayed
   * @description This function replaces or adds some display text
   * and sets a link on this text.
   * It will also handle cases where there is no link or display text.
   */
  const setLink = (editor: Editor, link?: string, displayText?: string) => {
    let selection = editor.view.state.selection;
    if (editor.isActive('editorLink')) {
      selection = selectLink(editor);
    }
    if (!link) {
      // No link, only display text
      if (displayText) {
        editor.commands.unsetLink();
        editor.commands.focus(selection.to);
        return;
      }
      // No link, no display text
      else {
        editor.commands.deleteSelection();
        editor.commands.focus(selection.from);
        return;
      }
    }
    // No display text, only link
    if (!displayText) {
      editor.commands.insertContentAt(
        { from: selection.from, to: selection.to },
        link && `<a href="${link}">${link}</a>`,
      );
      editor.commands.focus(selection.to + link.length);
      return;
    }
    editor.commands.insertContentAt(
      { from: selection.from, to: selection.to },
      link ? `<a href="${link}">${displayText}</a>` : displayText,
    );
    editor.commands.focus(selection.to);
  };

  /**
   *
   * @param editor Editor
   * @returns {string} Selected link
   * @description This function selects a link under the cursor
   * and returns the selected link.
   */
  const selectLink = (editor: Editor) => {
    editor.chain().focus().extendMarkRange('editorLink').run();
    return editor.view.state.selection;
  };

  /**
   *
   * @param editor Editor
   * @returns {string} Selected text
   * @description This function returns the selected text
   * in the editor.
   */
  const getSelection = (editor: Editor) => {
    const { view, state } = editor;
    const { from, to } = view.state.selection;
    return state.doc.textBetween(from, to, '');
  };

  /**
   *
   * @param editor Editor
   * @returns {string} Link
   * @description This function returns the active link in the editor.
   */
  const getLink = (editor: Editor) => {
    const link = editor.getAttributes('editorLink').href;
    return link;
  };

  /**
   *
   * @param editor Editor
   * @returns {string} Display text
   * @description This function returns the display text
   * of the active link in the editor.
   * If the cursor is in the linked text, but no text is selected,
   * it will still return the linked text.
   */
  const getDisplayTextFromLink = (editor: Editor) => {
    let selection = getSelection(editor);
    if (selection) return selection;
    const { state } = editor;
    const { $from, $to } = state.selection;

    const before = $from.nodeBefore ? $from.nodeBefore.textContent : '';
    const after = $to.nodeAfter ? $to.nodeAfter.textContent : '';
    selection = before + after;
    return selection;
  };

  /**
   *
   * @param editor Editor
   * @returns {boolean} True if the button should be disabled
   * @description This function checks if the add link button should be disabled.
   * We disable the button if a link is active in the editor.
   * This prevents adding a link to an existing link.
   */
  const buttonIsDisabled = (editor: Editor) => {
    if (!buttonRef) return true;
    // When you click on the button, the editor loses focus
    // This causes the button's onClick callback to not fire
    // So only disable the button if the button is not focused
    if (!editor.isFocused && document.activeElement !== buttonRef) {
      return true;
    }
    return !!getLink(editor);
  };

  /**
   *
   * @param e React.MouseEvent<HTMLElement>
   * @description This function sets the isAddingLink state to true to enable the BubbleMenu.
   */
  const handleAddLink = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setIsAddingLink(true);
  };

  // This is necessary to get the position of the bubble menu when no text is selected
  /**
   *
   * @param editor Editor
   * @returns {DOMRect} Position to render the bubble menu
   * @description This function returns the position to render the bubble menu.
   * Normally, this would be handled automatically, but we need to handle the case
   * when no text is selected.
   */
  const getPosition = (editor: Editor) => {
    if (getSelection(editor)) {
      return posToDOMRect(
        editor.view,
        editor.view.state.selection.from,
        editor.view.state.selection.to,
      );
    }
    return posToDOMRect(
      editor.view,
      editor.view.state.selection.from,
      editor.view.state.selection.from,
    );
  };

  const onSubmitLink = (
    editor: Editor,
    link?: string,
    displayText?: string,
  ) => {
    setLink(editor, link, displayText);
    setIsAddingLink(false);
    editor.commands.focus('end');
  };

  const onCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    editor && editor.commands.focus('end');
    setIsAddingLink(false);
  };

  const onHideBubbleMenu = () => {
    setIsAddingLink(false);
    buttonRef?.blur();
  };

  const shouldShowBubbleMenu = () => {
    return isAddingLink;
  };

  const shouldShowFloatingMenu = (editor: Editor) => {
    return editor.isActive('editorLink') || false;
  };

  if (!editor) return null;
  return (
    <div css={[containerStyles, isInvalid && invalidStyles]}>
      {buttonRef && (
        <BubbleMenu
          editor={editor}
          shouldShow={shouldShowBubbleMenu}
          tippyOptions={{
            duration: 200,
            placement: 'auto',
            trigger: 'click',
            triggerTarget: buttonRef,
            getReferenceClientRect: () => getPosition(editor),
            onClickOutside: () => onHideBubbleMenu(),
          }}
        >
          {isAddingLink && (
            <AddLinkPopup
              editor={editor}
              onSubmitForm={onSubmitLink}
              onCancel={onCancel}
              selectedLink={getLink(editor)}
              selectedText={getSelection(editor)}
            />
          )}
        </BubbleMenu>
      )}
      <FloatingMenu
        editor={editor}
        shouldShow={() => shouldShowFloatingMenu(editor)}
        tippyOptions={{
          duration: 0,
          placement: 'auto',
          onClickOutside: () => onHideBubbleMenu(),
        }}
      >
        <AddLinkPopup
          editor={editor}
          onSubmitForm={onSubmitLink}
          onCancel={onCancel}
          selectedLink={getLink(editor)}
          selectedText={getDisplayTextFromLink(editor)}
        />
      </FloatingMenu>
      <div css={editorContainerStyles}>
        <EditorContent editor={editor} />
      </div>
      <div css={toolbarStyles}>
        <div css={characterProgressStyles}>
          <p
            css={characterCountTextStyles}
          >{`${editor.storage.characterCount.characters()}/${CHARACTER_LIMIT}`}</p>
          <ProgressCircle
            percentage={
              editor.storage.characterCount.characters() / CHARACTER_LIMIT
            }
          />
        </div>
        <UnstyledButton
          css={[
            unstyledButtonStyles,
            buttonIsDisabled(editor) && disabledButtonStyles,
          ]}
          onClick={handleAddLink}
          disabled={buttonIsDisabled(editor)}
          ref={(buttonRef) => setButtonRef(buttonRef)}
        >
          {buttonIsDisabled(editor) || isAddingLink ? (
            <img src={AddLinkDisabled} css={addLinkIconStyles} />
          ) : (
            <img src={AddLink} css={addLinkIconStyles} />
          )}

          <p
            css={[
              addLinkTextStyles,
              buttonIsDisabled(editor) && disabledLinkTextStyles,
              isAddingLink && disabledLinkTextStyles,
            ]}
          >
            {'Add a link'}
          </p>
        </UnstyledButton>
      </div>
    </div>
  );
};

const containerStyles = css({
  border: `1px solid ${token('color.border.input')}`,
  borderRadius: '4px',
  '&:focus-within': {
    margin: '-1px',
    border: `2px solid ${token('color.border.focused')}`,
  },
});

const invalidStyles = css({
  border: '2px solid #C9372C',
  margin: '-1px',
});

const editorContainerStyles = css({
  padding: '8px 12px',
  // Disabling eslint rule because we need to style the editor
  /* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
  '.tiptap': {
    minHeight: '108px',
    lineHeight: '24px',
    minWidth: '100%',
    fontFamily: 'Inter',
    fontSize: '16px',
  },
  '.ProseMirror:focus': {
    outline: 'none',
    border: 'none',
    outlineColor: 'none',
  },
  // Re-enable the rule
  /* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */
});

const toolbarStyles = css({
  borderTop: `1px solid ${token('color.border')}`,
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingTop: '10px',
  paddingBottom: '10px',
  paddingRight: '16px',
});

const characterProgressStyles = css({
  display: 'flex',
  padding: `${token('space.050')} ${token('space.150')} ${token(
    'space.050',
  )} 0`,
  alignItems: 'center',
  borderRight: `1px solid ${token('color.border')}`,
  marginRight: token('space.150'),
});

const characterCountTextStyles = css({
  marginRight: `${token('space.075')}`,
  fontFamily: 'Inter',
  color: `${token('color.text.subtlest')}`,
});

const unstyledButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  background: 'none',
  color: 'inherit',
  border: 'none',
  padding: `${token('space.050')} ${token('space.150')}`,
  cursor: 'pointer',
});

const disabledButtonStyles = css({
  pointerEvents: 'none',
});

const addLinkIconStyles = css({
  marginRight: '4px',
  width: '24px',
  height: '24px',
  color: token('color.icon.selected'),
});

const addLinkTextStyles = css({
  margin: 0,
  color: token('color.text.brand'),
});

const disabledLinkTextStyles = css({
  color: token('color.text.disabled'),
});
